import React, { useEffect, useState } from "react";

import { ChevronDownIcon, ClockCircleIcon, EyeIcon, InfoIcon, NaCircleIcon, PhoneIcon, WarningTriangleIcon } from "../../lib/icons";
import { VALID_CITIES } from "../leads/LeadFilters";
import {
  displayPhone,
  formatDateCell,
  formatTimeLeft,
  formatTimeSince,
  startCase,
  timeLeftStatus,
  toneForSource,
} from "../../lib/utils";

const PHONE_REVEAL_STATE_KEY = "crm.leads.revealedPhones.v1";

const LOCATION_KEYWORDS = ['city', 'state', 'pin', 'zip', 'location', 'district', 'area', 'taluk', 'tehsil', 'mandal', 'block'];

function extractFbLocationData(lead) {
  const fb = lead.fb_location || (lead.custom_fields?.facebook) || {};
  const fieldData = fb.field_data || {};
  const results = [];
  const seen = new Set();

  const push = (label, value) => {
    const v = String(value ?? '').trim();
    if (!v || /^(other|others|na|n\/a|-)$/i.test(v)) return;
    const key = v.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    results.push({ label, value: v });
  };

  // Top-level meta fields
  push('Raw City', fb.raw_city);
  push('Home City', fb.home_city);

  // Scan field_data for any location-bearing key
  Object.entries(fieldData).forEach(([k, v]) => {
    const kl = k.toLowerCase().replace(/_/g, ' ');
    if (LOCATION_KEYWORDS.some((kw) => kl.includes(kw))) {
      push(k.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()), v);
    }
  });

  return results;
}
const CITY_QUALIFIER_WORDS = new Set([
  'greater', 'new', 'old', 'navi', 'east', 'west', 'north', 'south', 'central',
  'inner', 'outer', 'upper', 'lower', 'mini', 'cantt', 'cantonment', 'fort',
  'nagar', 'vihar', 'enclave', 'puram', 'abad', 'ganj', 'mandi', 'chowk', 'road',
  'colony', 'township', 'hills', 'valley', 'junction', 'station', 'port', 'metro',
]);

function looksLikePersonName(cityName) {
  if (!cityName) return false;
  const words = cityName.trim().split(/\s+/);
  if (words.length < 2 || words.length > 3) return false;
  return (
    words.every((w) => /^[A-Za-z]{3,15}$/.test(w)) &&
    !words.some((w) => CITY_QUALIFIER_WORDS.has(w.toLowerCase()))
  );
}

function readStoredPhoneRevealState() {
  if (typeof window === "undefined") {
    return { revealedPhones: {}, maskedRevealedPhones: {} };
  }

  try {
    const parsed = JSON.parse(window.sessionStorage.getItem(PHONE_REVEAL_STATE_KEY) || "{}");
    return {
      revealedPhones: parsed.revealedPhones && typeof parsed.revealedPhones === "object"
        ? parsed.revealedPhones
        : {},
      maskedRevealedPhones: parsed.maskedRevealedPhones && typeof parsed.maskedRevealedPhones === "object"
        ? parsed.maskedRevealedPhones
        : {},
    };
  } catch (_error) {
    return { revealedPhones: {}, maskedRevealedPhones: {} };
  }
}

function formatLeadDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d)) return "—";
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

function formatLeadTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d)) return "—";
  return d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true });
}

function PhoneRevealEyeGlyph() {
  return (
    <svg viewBox="0 0 20 20" aria-hidden="true" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round">
      <path d="M10 4.5c-4 0-7.3 3-8.5 5.5 1.2 2.5 4.5 5.5 8.5 5.5s7.3-3 8.5-5.5C17.3 7.5 14 4.5 10 4.5Z" />
      <circle cx="10" cy="10" r="2.2" fill="currentColor" stroke="none" />
    </svg>
  );
}

function PhoneMaskEyeGlyph() {
  return (
    <svg viewBox="0 0 20 20" aria-hidden="true" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 3l14 14" />
      <path d="M8.4 4.8A8.9 8.9 0 0 1 10 4.5c4 0 7.3 3 8.5 5.5a12 12 0 0 1-2.2 2.8" />
      <path d="M13 14.2a7.8 7.8 0 0 1-3 .8c-4 0-7.3-3-8.5-5.5a12.7 12.7 0 0 1 3.4-3.6" />
      <path d="M8.6 8.6a2.2 2.2 0 0 0 2.8 2.8" />
    </svg>
  );
}

function PhoneCell({ lead, roleScope, revealedPhone, isRevealed, onPhoneMasked, onPhoneRevealed }) {
  const [confirming, setConfirming] = useState(false);
  const [loading, setLoading] = useState(false);

  const canSeeFullPhone = roleScope?.can_see_full_phone;
  const displayedPhone = canSeeFullPhone
    ? (lead.phone_number || lead.lead_phone_number || "—")
    : (isRevealed
        ? (revealedPhone || "")
        : displayPhone(lead, roleScope));

  async function handleReveal() {
    setLoading(true);
    try {
      const res = await fetch(`/api/crm/leads/${lead.id}/reveal-phone/`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" },
      });
      if (res.ok) {
        const data = await res.json();
        onPhoneRevealed(lead.id, data.phone_number || lead.phone_number || lead.lead_phone_number || "");
      }
    } finally {
      setLoading(false);
      setConfirming(false);
    }
  }

  if (canSeeFullPhone) {
    return <span style={{ fontFamily: "monospace", whiteSpace: "nowrap" }}>{displayedPhone}</span>;
  }

  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4, whiteSpace: "nowrap" }}>
      <span style={{ fontFamily: "monospace", letterSpacing: "0.05em" }}>
        {isRevealed ? (revealedPhone || displayedPhone) : displayedPhone}
      </span>
      {isRevealed ? (
        <button
          type="button"
          title="Mask phone number"
          onClick={(e) => { e.stopPropagation(); onPhoneMasked(lead.id); }}
          style={{
            background: "#eef2ff",
            border: "1px solid #c5cff7",
            borderRadius: "4px",
            cursor: "pointer",
            color: "#4f6bed",
            padding: "2px 5px",
            lineHeight: 1,
            display: "inline-flex",
            alignItems: "center",
            flexShrink: 0,
          }}
        >
          <PhoneRevealEyeGlyph />
        </button>
      ) : (
        <button
          type="button"
          title="Reveal phone number (will be logged)"
          onClick={(e) => { e.stopPropagation(); setConfirming(true); }}
          style={{
            background: "#eef2ff",
            border: "1px solid #c5cff7",
            borderRadius: "4px",
            cursor: "pointer",
            color: "#4f6bed",
            padding: "2px 5px",
            lineHeight: 1,
            display: "inline-flex",
            alignItems: "center",
            flexShrink: 0,
          }}
        >
          <PhoneMaskEyeGlyph />
        </button>
      )}
      {confirming && (
        <div
          className="modal-backdrop"
          style={{ zIndex: 1200 }}
          onClick={(e) => { e.stopPropagation(); setConfirming(false); }}
        >
          <div
            className="modal-panel"
            style={{ maxWidth: 360 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <div>
                <p className="modal-kicker">Privacy Notice</p>
                <h2 style={{ fontSize: "1rem" }}>Reveal phone number?</h2>
              </div>
            </div>
            <p style={{ fontSize: 13, color: "var(--text-secondary)", margin: "0.5rem 0 1.25rem" }}>
              This action will be logged and attributed to your account. Only proceed if you need the full number.
            </p>
            <div className="modal-actions">
              <button
                type="button"
                className="secondary-ghost"
                onClick={(e) => { e.stopPropagation(); setConfirming(false); }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="primary-button modal-submit"
                disabled={loading}
                onClick={(e) => { e.stopPropagation(); handleReveal(); }}
              >
                {loading ? "Loading..." : "Yes, show number"}
              </button>
            </div>
          </div>
        </div>
      )}
    </span>
  );
}

function LeadsTable({
  canDeleteLead,
  currentPage,
  leads,
  loading,
  pageSize,
  pageSizeOptions,
  paginationItems,
  roleScope,
  selectedLeadId,
  showOthersDetail,
  sourceMap,
  totalCount,
  onDeleteLead,
  onEditLead,
  onLogCall,
  onOpenJourney,
  onPageChange,
  onPageSizeChange,
  onSelectLead,
}) {
  // Base columns: Lead ID, Lead Date, Lead Time, Source, Name, Mobile, Call, City, Assigned, Attempted Calls, Connected Calls, Lead Stage, Last Contacted, View = 14
  // + 1 Delete column if admin
  const totalColumns = canDeleteLead ? 15 : 14;
  const [revealedPhones, setRevealedPhones] = useState(() => readStoredPhoneRevealState().revealedPhones);
  const [maskedRevealedPhones, setMaskedRevealedPhones] = useState(() => readStoredPhoneRevealState().maskedRevealedPhones);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    window.sessionStorage.setItem(
      PHONE_REVEAL_STATE_KEY,
      JSON.stringify({ revealedPhones, maskedRevealedPhones }),
    );
  }, [revealedPhones, maskedRevealedPhones]);

  function handlePhoneRevealed(leadId, phoneNumber) {
    setRevealedPhones((current) => ({ ...current, [leadId]: phoneNumber || "" }));
    setMaskedRevealedPhones((current) => ({ ...current, [leadId]: false }));
  }

  function handlePhoneMasked(leadId) {
    setMaskedRevealedPhones((current) => ({ ...current, [leadId]: true }));
  }

  return (
    <section className="table-card leads-table-card">
      <div className="table-scroll">
        <table>
          <thead>
            <tr>
              <th>Lead ID</th>
              <th>Lead Date</th>
              <th>Lead Time</th>
              <th>Source</th>
              <th>Name</th>
              <th>Mobile</th>
              <th>Call</th>
              <th>City</th>
              <th>Assigned</th>
              <th>Attempted Calls</th>
              <th>Connected Calls</th>
              <th>Lead Stage</th>
              <th>Last Contacted</th>
              <th>View</th>
              {canDeleteLead && <th>Delete</th>}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={totalColumns} className="table-state">
                  Loading leads...
                </td>
              </tr>
            ) : null}

            {!loading && !leads.length ? (
              <tr>
                <td colSpan={totalColumns} className="table-state">
                  No leads matched the current filters.
                </td>
              </tr>
            ) : null}

            {!loading &&
              leads.map((lead) => {
                const createdAt = lead.registered_at || lead.created_at;
                const noFirstCall = !lead.last_contacted_at;
                const createdMs = new Date(createdAt).getTime();
                const slaBreached = noFirstCall && (Date.now() - createdMs) > 24 * 60 * 60 * 1000;
                return (
                  <tr
                    key={lead.id}
                    className={lead.id === selectedLeadId ? "row-selected" : ""}
                    onClick={() => onSelectLead(lead.id)}
                  >
                    {/* Lead ID */}
                    <td className="mono-text">
                      <button
                        type="button"
                        className="name-button"
                        style={{ fontFamily: "monospace", textDecoration: "underline" }}
                        onClick={(event) => { event.stopPropagation(); onOpenJourney(lead.id); }}
                      >
                        {lead.client_id}
                      </button>
                    </td>

                    {/* Lead Date */}
                    <td className="date-cell" style={{ whiteSpace: "nowrap" }}>
                      {formatLeadDate(createdAt)}
                    </td>

                    {/* Lead Time */}
                    <td className="date-cell" style={{ whiteSpace: "nowrap" }}>
                      {formatLeadTime(createdAt)}
                    </td>

                    {/* Source */}
                    <td>
                      <span className={`pill pill-${toneForSource(lead.source_name, sourceMap[lead.source]?.source_type)}`}>
                        {lead.source_name || "Others"}
                      </span>
                    </td>

                    {/* Name */}
                    <td className="name-cell">
                      <button
                        type="button"
                        className="name-button"
                        onClick={(event) => { event.stopPropagation(); onOpenJourney(lead.id); }}
                      >
                        {lead.full_name}
                      </button>
                    </td>

                    {/* Mobile */}
                    <td className="mobile-cell">
                      <PhoneCell
                        lead={lead}
                        roleScope={roleScope}
                        revealedPhone={revealedPhones[lead.id]}
                        isRevealed={Boolean(revealedPhones[lead.id]) && !maskedRevealedPhones[lead.id]}
                        onPhoneMasked={handlePhoneMasked}
                        onPhoneRevealed={handlePhoneRevealed}
                      />
                    </td>

                    {/* Call icon */}
                    <td>
                      {roleScope?.can_call ? (
                        <button
                          type="button"
                          className={`call-button${slaBreached ? " call-button-alert" : ""}`}
                          aria-label={slaBreached ? "Overdue: call lead now" : "Call lead"}
                          title={slaBreached ? "No first call after 24h — please call now" : "Call lead"}
                          onClick={(event) => { event.stopPropagation(); onLogCall(lead.id); }}
                        >
                          <PhoneIcon />
                          {slaBreached ? <span className="call-button-alert-badge" aria-hidden="true">!</span> : null}
                        </button>
                      ) : (
                        <span className="muted-action">—</span>
                      )}
                    </td>

                    {/* City */}
                    <td className="subtext-cell">
                      {(() => {
                        const city = lead.city_name;
                        const rawFbCity = lead.custom_fields?.facebook?.raw_city
                          || (looksLikePersonName(lead.city_name) ? lead.city_name : null);
                        const isOthers = !city || city === 'Others' || looksLikePersonName(city);
                        if (isOthers) {
                          const locData = showOthersDetail
                            ? extractFbLocationData(lead)
                            : [];
                          const locationBadge = locData.length > 0
                            ? {
                                title: locData.map(({ label, value }) => `${label}: ${value}`).join('\n'),
                                values: locData.map(({ value }) => value),
                              }
                            : rawFbCity
                              ? {
                                  title: `Meta reported city: ${rawFbCity}`,
                                  values: [rawFbCity],
                                }
                              : null;
                          return (
                            <span>
                              Others
                              {locationBadge ? (
                                <span
                                  title={locationBadge.title}
                                  style={{
                                    display: 'inline-block',
                                    marginLeft: '0.35em',
                                    padding: '2px 7px',
                                    fontSize: '0.7em',
                                    borderRadius: '10px',
                                    background: '#eff6ff',
                                    color: '#1d4ed8',
                                    border: '1px solid #bfdbfe',
                                    verticalAlign: 'middle',
                                    cursor: 'default',
                                    whiteSpace: 'nowrap',
                                    maxWidth: '120px',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                  }}
                                >
                                  📍 {locationBadge.values.join(' · ')}
                                </span>
                              ) : null}
                            </span>
                          );
                        }
                        if (VALID_CITIES.includes(city)) return city;
                        return (
                          <span>
                            {city}
                            <span
                              title="New city — no agents assigned yet"
                              style={{
                                display: 'inline-block',
                                marginLeft: '0.35em',
                                padding: '1px 5px',
                                fontSize: '0.68em',
                                borderRadius: '10px',
                                background: 'var(--tone-warning-bg, #fff7e6)',
                                color: 'var(--tone-warning, #b45309)',
                                border: '1px solid var(--tone-warning-border, #fbbf24)',
                                verticalAlign: 'middle',
                              cursor: 'default',
                            }}
                          >
                            new
                            </span>
                          </span>
                        );
                      })()}
                    </td>

                    {/* Assigned agent */}
                    <td className="subtext-cell">
                      {lead.assigned_agent?.full_name || "Unassigned"}
                    </td>

                    {/* Attempted Calls */}
                    <td className="subtext-cell">{lead.call_count ?? 0}</td>

                    {/* Connected Calls */}
                    <td className="subtext-cell">{lead.connected_call_count ?? 0}</td>

                    {/* Lead Stage (status) */}
                    <td className="subtext-cell">{startCase(lead.status) || "—"}</td>

                    {/* Last Contacted */}
                    <td className="date-cell">{formatDateCell(lead.last_contacted_at)}</td>

                    {/* View */}
                    <td>
                      <button
                        type="button"
                        className="edit-button"
                        aria-label="View lead"
                        onClick={(event) => { event.stopPropagation(); onOpenJourney(lead.id); }}
                      >
                        <EyeIcon />
                      </button>
                    </td>

                    {/* Delete (admin only) */}
                    {canDeleteLead && (
                      <td>
                        <button
                          type="button"
                          aria-label="Delete lead"
                          title="Delete lead permanently"
                          onClick={(event) => { event.stopPropagation(); onDeleteLead(lead.id); }}
                          style={{
                            display: "flex", alignItems: "center", justifyContent: "center",
                            width: 30, height: 30, borderRadius: 6,
                            border: "1.5px solid #fecaca", background: "#fff5f5",
                            color: "#ef4444", cursor: "pointer",
                          }}
                        >
                          <svg width="14" height="14" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>

      <div className="table-footer">
        <div className="footer-summary">
          <span>
            {totalCount
              ? `${(currentPage - 1) * pageSize + 1}-${Math.min(currentPage * pageSize, totalCount)}`
              : "0"}{" "}
            of {totalCount} leads
          </span>
          <label className="page-size-control">
            <span className="page-size-label">Show</span>
            <div className="page-size-select-shell">
              <select
                aria-label="Select leads per page"
                value={pageSize}
                onChange={(event) => onPageSizeChange(Number(event.target.value))}
              >
                {pageSizeOptions.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
              <ChevronDownIcon />
            </div>
            <span className="page-size-label">leads</span>
          </label>
        </div>
        <div className="pagination">
          <button
            type="button"
            className="page-nav"
            disabled={currentPage <= 1}
            onClick={() => onPageChange(currentPage - 1)}
          >
            Prev
          </button>
          {paginationItems.map((item, index) =>
            item === "ellipsis" ? (
              <span key={`${item}-${index}`} className="page-gap">
                ...
              </span>
            ) : (
              <button
                key={item}
                type="button"
                className={`page-pill ${item === currentPage ? "active" : ""}`}
                onClick={() => onPageChange(item)}
              >
                {item}
              </button>
            ),
          )}
          <button
            type="button"
            className="page-nav"
            disabled={currentPage >= Math.max(Math.ceil(totalCount / pageSize), 1)}
            onClick={() => onPageChange(currentPage + 1)}
          >
            Next
          </button>
        </div>
      </div>
    </section>
  );
}

export default LeadsTable;
